/**
 * The four things a screen can be showing besides content.
 *
 * These exist as components rather than as inline markup so that "loading",
 * "empty", "error" and "offline" look and behave the same everywhere. An app
 * where every screen invents its own empty state is an app that feels
 * assembled rather than designed.
 *
 * The rule for empty states: say what would be here, and — if the reader can
 * do something about it — give them the control. The rule for errors: say what
 * failed and offer the retry. Never both apologise and stop.
 */

import type { ReactNode } from 'react';
import { AlertTriangle, CloudOff, Inbox, RefreshCw } from 'lucide-react';

import { describeError } from '../../data/firestoreHooks';
import type { FirestoreError } from 'firebase/firestore';

export function EmptyState({
  icon,
  title,
  body,
  action,
}: {
  icon?: ReactNode;
  title: string;
  body?: string;
  action?: ReactNode;
}) {
  return (
    <div className="state">
      <div className="state-icon" aria-hidden="true">
        {icon ?? <Inbox size={24} />}
      </div>
      <p className="state-title">{title}</p>
      {body ? <p className="state-body">{body}</p> : null}
      {action ? <div style={{ marginBlockStart: 'var(--s-2)' }}>{action}</div> : null}
    </div>
  );
}

export function ErrorState({
  error,
  onRetry,
  title = "That didn't load",
}: {
  error: FirestoreError | Error | null;
  onRetry?: () => void;
  title?: string;
}) {
  return (
    <div className="state">
      <div className="state-icon" aria-hidden="true" style={{ color: 'var(--danger)' }}>
        <AlertTriangle size={24} />
      </div>
      <p className="state-title">{title}</p>
      <p className="state-body">{describeError(error)}</p>
      {/*
        Which read failed. Deliberately faint and terse — it is a query name,
        not a stack trace, and it is what makes a phone-only bug report
        actionable.
      */}
      {(error as { source?: string } | null)?.source ? (
        <p className="faint t-xs">{(error as { source?: string }).source}</p>
      ) : null}
      {onRetry ? (
        <button type="button" className="btn btn--secondary" onClick={onRetry}>
          <RefreshCw size={16} />
          Try again
        </button>
      ) : null}
    </div>
  );
}

/**
 * The stale-data notice.
 *
 * Shown *above* content rather than instead of it. Data from the on-disk cache
 * is real data — hiding it behind a spinner because the network is down is how
 * an offline-capable app ends up feeling worse than a static page.
 */
export function OfflineNotice() {
  return (
    <div
      className="row"
      style={{
        gap: 'var(--s-2)',
        padding: 'var(--s-2) var(--s-3)',
        borderRadius: 'var(--radius-md)',
        background: 'var(--surface-sunken)',
        color: 'var(--ink-secondary)',
        fontSize: 'var(--text-sm)',
        marginBlockEnd: 'var(--s-4)',
      }}
    >
      <CloudOff size={15} aria-hidden="true" />
      <span>Offline — showing what was saved on this device.</span>
    </div>
  );
}

/**
 * Skeletons.
 *
 * Each variant is sized to the component it stands in for, because the whole
 * point is that nothing moves when the real thing arrives. A generic grey box
 * that is the wrong height causes exactly the layout jump it was meant to
 * prevent.
 */
export function SkeletonText({ lines = 3, width = '100%' }: { lines?: number; width?: string }) {
  return (
    <div aria-hidden="true">
      {Array.from({ length: lines }, (_, i) => (
        <div
          key={i}
          className="skeleton skeleton--text"
          // Last line short, like real text.
          style={{ width: i === lines - 1 ? '62%' : width }}
        />
      ))}
    </div>
  );
}

export function SkeletonCard() {
  return (
    <div className="card" aria-hidden="true">
      <div className="skeleton" style={{ width: 90, height: 12, marginBlockEnd: 'var(--s-4)' }} />
      <div className="skeleton" style={{ width: '70%', height: 24, marginBlockEnd: 'var(--s-3)' }} />
      <SkeletonText lines={2} />
    </div>
  );
}

export function SkeletonRows({ count = 4 }: { count?: number }) {
  return (
    <div className="panel rows" aria-hidden="true">
      {Array.from({ length: count }, (_, i) => (
        <div key={i} className="row-item">
          <div className="skeleton" style={{ width: 36, height: 36, borderRadius: 'var(--radius-pill)' }} />
          <div style={{ flex: 1 }}>
            <div className="skeleton" style={{ width: '46%', height: 13, marginBlockEnd: 6 }} />
            <div className="skeleton" style={{ width: '72%', height: 11 }} />
          </div>
        </div>
      ))}
    </div>
  );
}

/**
 * Screen-level loading.
 *
 * Deliberately quiet and centred rather than a full skeleton: used only for
 * the route transition, where the shape of what is coming is not yet known.
 */
export function RouteFallback() {
  return (
    <div
      style={{
        display: 'grid',
        placeItems: 'center',
        minHeight: '50dvh',
      }}
      role="status"
      aria-label="Loading"
    >
      <span className="spinner" style={{ width: 22, height: 22 }} />
    </div>
  );
}
